import { FiX } from "react-icons/fi";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { IdxSettings } from "gql/generated.graphql";
import { useEffect, useState } from "react";
import ColorPicker from "./color-picker";
import { useImmer } from "use-immer";
import { Toggle } from "dash-ui";

// Form Schema with zod
const IdxSettingsFormSchema = z.object({
  websiteDomain: z
    .string()
    .min(1, "Website Domain is required.")
    .refine(
      (url) =>
        /^(https?:\/\/)/.test(url) &&
        /^https?:\/\/[^\s/$.?#].[^\s]*$/.test(url),
      {
        message: "URL must start with http:// or https:// and be a valid URL.",
      }
    ),
  branding: z
    .object({
      mainColor: z.string().optional(),
      textColor: z.string().optional(),
      backgroundColor: z.string().optional(),
    })
    .optional(),
});

// ==> Types

export type IdxSettingsFormData = z.infer<typeof IdxSettingsFormSchema>;

type IdxSettingsModalProps = {
  onClose?: () => void;
  onSave?: (data: IdxSettingsFormData) => Promise<void>;
  idxSettings?: IdxSettings | null;
};

type BrandingColors = {
  mainColor?: string;
  textColor?: string;
  backgroundColor?: string;
};

// Initial state for branding colors
const initialColors: BrandingColors = {
  mainColor: "#1b75bb",
  textColor: "#334155",
  backgroundColor: "#f8fafc",
};

// Main Component
export function IdxSettingsModal(props: IdxSettingsModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [colors, setColors] = useImmer<BrandingColors>(initialColors);
  const [isWixSite, setIsWixSite] = useState<boolean>(false);

  //Hooks
  const idxSettingsForm = useForm<IdxSettingsFormData>({
    resolver: zodResolver(IdxSettingsFormSchema),
    mode: "onChange",
  });

  // Function to handle save
  async function save(data: IdxSettingsFormData) {
    setLoading(true);
    // Formatting url if is a Wix site
    let domain = data.websiteDomain;
    if (isWixSite) {
      domain = domain.replace(/\./g, "-").replace(/\/$/, "") + ".filesusr.com/";
    }
    if (props.onSave)
      await props.onSave({
        branding: {
          mainColor: colors.mainColor,
          textColor: colors.textColor,
          backgroundColor: colors.backgroundColor,
        },
        websiteDomain: domain,
      });
    if (props.onClose) props.onClose();
    setLoading(false);
  }

  const resetColors = () => {
    setColors(initialColors);
  };

  // UseEffect to set saved data
  useEffect(() => {
    if (props.idxSettings) {
      idxSettingsForm.setValue(
        "websiteDomain",
        props.idxSettings.websiteDomain || ""
      );
      setColors((draft) => {
        draft.mainColor = props.idxSettings?.branding.mainColor;
        draft.textColor = props.idxSettings?.branding.textColor;
        draft.backgroundColor = props.idxSettings?.branding.backgroundColor;
      });
    }
  }, [props.idxSettings]);

  // HTML Component
  return (
    <div className="fixed w-screen h-screen bg-black/30 top-0 left-0 z-10 flex items-center justify-center">
      <form
        onSubmit={idxSettingsForm.handleSubmit(save)}
        className="bg-white shadow-2xl rounded-lg p-8 flex flex-col w-full max-w-screen-sm gap-6 relative"
      >
        <button
          type="button"
          onClick={props.onClose}
          className="absolute size-8 flex items-center justify-center rounded-lg hover:bg-black/5 ml-auto top-4 right-4"
        >
          <FiX />
        </button>
        <h2 className="font-bold">Exchange Settings</h2>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-slate-500">Website Domain*</label>
            <input
              type="text"
              className={`h-10 px-4 border ${
                idxSettingsForm.formState.errors.websiteDomain
                  ? "border-red-600"
                  : "border-slate-300"
              } rounded-lg`}
              placeholder="https://www.google.com/"
              {...idxSettingsForm.register("websiteDomain")}
            />
            {idxSettingsForm.formState.errors.websiteDomain && (
              <span className="text-sm text-red-700">
                {idxSettingsForm.formState.errors.websiteDomain.message}
              </span>
            )}
            <span className="text-sm text-sky-700">
              Enter the url of the website that will render the Exchange feed.
            </span>
          </div>
          {idxSettingsForm.formState.dirtyFields.websiteDomain && (
            <div className="flex items-center justify-between">
              <div>
                <p>Wix Site</p>
                <p className="text-slate-500 text-sm">
                  Check this option if your site was created with{" "}
                  <a href="https://www.wix.com" target="_blank">
                    Wix
                  </a>{" "}
                  so we can correctly format the address for you.
                </p>
              </div>
              <Toggle
                checked={isWixSite}
                onChange={() => setIsWixSite(!isWixSite)}
              />
            </div>
          )}
        </div>

        {/* Color Selectors */}
        <h2 className="font-bold">Branding Colors</h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
          <ColorPicker
            label="Main Color"
            color={colors.mainColor}
            setColor={(value) =>
              setColors((draft) => {
                draft.mainColor = value;
              })
            }
          />
          <ColorPicker
            label="Text Color"
            color={colors.textColor}
            setColor={(value) =>
              setColors((draft) => {
                draft.textColor = value;
              })
            }
          />
          <ColorPicker
            label="Background Color"
            color={colors.backgroundColor}
            setColor={(value) =>
              setColors((draft) => {
                draft.backgroundColor = value;
              })
            }
          />
        </div>

        <footer className="flex gap-4 justify-end">
          <button
            type="reset"
            onClick={props.onClose}
            className="h-10 border border-slate-300 px-4 rounded-lg hover:bg-black/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={resetColors}
            className="h-10 border border-slate-300 px-2 md:px-4 rounded-lg hover:bg-black/5 truncate"
          >
            Reset Colors
          </button>
          <button
            disabled={loading}
            type="submit"
            className="h-10 px-4 rounded-lg bg-sky-600 text-white hover:brightness-110 disabled:opacity-30"
          >
            Save
          </button>
        </footer>
      </form>
    </div>
  );
}
